import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useStytchB2BClient } from '@stytch/react/b2b';
import { revalidateLogic, useStore } from '@tanstack/react-form';
import { z } from 'zod';

import { Button } from '@/components/buttons';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/Dialog';
import { useAppForm } from '@/components/Form';
import { FieldLabel } from '@/components/Form/fields';
import { maxPasswordLength } from '@/constants/numbers';
import { getStytchAPIErrorMessage } from '@/error/StytchError';
import { useNotification } from '@/providers/Notification';

type Props = {
  open: boolean;
  onClose: () => void;
};

const defaultValues = {
  password: '',
};

const UpdatePasswordDialog = ({ open, onClose }: Props) => {
  const { pushNotification } = useNotification();
  const stytch = useStytchB2BClient();
  const formSchema = z.object({
    password: z
      .string()
      .min(8, {
        message: t`Password must be at least 8 characters`,
      })
      .regex(/[a-z]/, {
        message: t`Password must contain at least one lowercase letter`,
      })
      .regex(/[A-Z]/, {
        message: t`Password must contain at least one uppercase letter`,
      })
      .regex(/[0-9]/, {
        message: t`Password must contain at least one number`,
      })
      .regex(/[^a-zA-Z0-9]/, {
        message: t`Password must contain at least one special character (e.g. !, @, #, $, %, ^, &, *)`,
      }),
  });

  const form = useAppForm({
    defaultValues,
    validationLogic: revalidateLogic(),
    validators: {
      onDynamic: formSchema,
      onSubmitAsync: async ({ value }) => {
        try {
          await stytch.passwords.resetBySession({
            password: value.password,
          });

          pushNotification({
            type: 'success',
            message: t`Your password has been updated. You have been logged out of all other devices.`,
          });

          handleClose();
        } catch (e) {
          pushNotification({
            type: 'error',
            message: getStytchAPIErrorMessage(e),
          });
        }
      },
    },
  });
  const isSaving = useStore(form.store, (state) => state.isSubmitting);

  const handleClose = () => {
    form.reset(undefined, { keepDefaultValues: true });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (isSaving) return;
        if (!isOpen) handleClose();
      }}
    >
      <DialogContent showCloseButton={!isSaving}>
        <DialogHeader>
          <DialogTitle>
            <Trans>Change Password</Trans>
          </DialogTitle>
          <DialogDescription>
            <Trans>
              Choose a new password for your account. The password must be at least 8 characters
              long and contain at least one lowercase letter, one uppercase letter, one number and
              one special character.
            </Trans>
          </DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-6 text-center"
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            form.handleSubmit();
          }}
        >
          <form.AppForm>
            <div className="mx-auto flex w-full max-w-100 flex-col gap-6 text-start">
              <form.AppField name="password">
                {(field) => (
                  <field.Field>
                    <FieldLabel htmlFor="update-password-new-password">
                      <Trans>New Password</Trans>
                    </FieldLabel>
                    <field.PasswordInput
                      id="update-password-new-password"
                      autoComplete="new-password"
                      maxLength={maxPasswordLength}
                      size="large"
                    />
                    <field.Error />
                  </field.Field>
                )}
              </form.AppField>
            </div>
            <DialogFooter>
              <DialogClose
                render={
                  <Button size="large" type="button" disabled={isSaving}>
                    <Trans>Cancel</Trans>
                  </Button>
                }
              />
              <form.SubmitButton size="large" variant="on-primary">
                <Trans>Update</Trans>
              </form.SubmitButton>
            </DialogFooter>
          </form.AppForm>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UpdatePasswordDialog;
